import React from 'react';
import PropTypes from 'prop-types';
import { intlShape, injectIntl } from 'react-intl';
import { DFPSlotsProvider, AdSlot } from 'react-dfp';
import get from 'lodash/get';
import Theme, { colors } from '../../themes';
import clientConfig from '../../../parameters/client/config';
import SectionIds from '../../tracking/SectionIds';
import { END_POINTS } from '../../sdk/tracking/Api';
import { getAdUnit } from '../../utils/adSense';

class WithThirdPartyAds extends React.PureComponent {
  constructor(props) {
    super(props);
    this.serpTopDfpBanner = React.createRef();
    this.serpBellyDfpBanner = React.createRef();
    this.serpBottomDfpBanner = React.createRef();
    this.requestToGoogCsa = this.requestToGoogCsa.bind(this);
    this.adBlockSlotsNumber = this.adBlockSlotsNumber.bind(this);
  }

  componentDidMount() {
    this.requestToGoogCsa(
      this.props.config.query,
      this.props.config.queryContextTranslationKey,
      this.props.adsCount,
      this.props.config.page
    );
  }

  componentDidUpdate(prevProps) {
    if (
      this.props.adsCount !== prevProps.adsCount ||
      this.props.config.query !== prevProps.config.query ||
      this.props.config.queryContextTranslationKey !==
        prevProps.config.queryContextTranslationKey ||
      this.props.config.page !== prevProps.config.page ||
      this.props.personalizedAds !== prevProps.personalizedAds
    ) {
      this.requestToGoogCsa(
        this.props.config.query,
        this.props.config.queryContextTranslationKey,
        this.props.adsCount,
        this.props.config.page,
        this.props.personalizedAds
      );
    }
  }

  getDfpProperties(adsCount) {
    const { isSemUrl, shouldShowDFP } = this.props;
    const dfpConfig = {
      top: {
        visible: false,
      },
      belly: {
        visible: false,
      },
      bottom: {
        sizes: ['fluid', [300, 250], [320, 50]],
        visible: shouldShowDFP,
      },
    };

    if (!isSemUrl && adsCount >= 20) {
      dfpConfig.belly.visible = shouldShowDFP;
      dfpConfig.belly.sizes = ['fluid', [320, 100], [320, 50]];
    }

    return dfpConfig;
  }

  requestToGoogCsa(query, queryContextTranslationKey, adsCount, page, personalizedAds) {
    const { isSemUrl, config, intl, pageviewId, appEnvironment } = this.props;

    const context = intl.formatMessage({ id: queryContextTranslationKey });
    const verticalName = config.verticalName;
    const settingIds = {
      homes: '8276baf0d2433bc2f501a19f65189a9f',
      cars: '839a266d6478a457c44d1b1495e9fdfc',
      jobs: '0fa63c2893b1ffa762a420b84754f581',
    };

    const pageOptions = {
      pubId: 'mobile-trovit',
      queryContext: context,
      query: `${context} ${query}`,
      channel: config.channel,
      hl: config.locale.slice(0, 2),
      linkTarget: '_blank',
      titleBold: true,
      settingsId: !config.paid ? settingIds[verticalName] : null,
      adsafe: 'medium',
      adtest: appEnvironment === 'production' ? 'off' : 'on',
      adPage: page,
      ie: 'utf-8',
      oe: 'utf-8',
      adstyle: 'hm',
      clickToCall: 'off',
      sellerRatings: 'off',
      siteLinks: 'off',
      personalizedAds,
    };

    const blockBaseConfig = {
      lines: 3,
      fontSizeTitle: '16px',
      fontSizeDescription: '13px',
      fontSizeDomainLink: '13px',
      colorTitleLink: '#2200C1',
      colorText: '#666666',
      colorDomainLink: '#327c50',
      colorBackground: '#FFFFFF',
      colorBorder: '#FFFFFF',
    };

    const buildTrackUrl = sectionId =>
      `${clientConfig.trackingApi}${
        END_POINTS.ADSENSE_CLICK
      }?section=${sectionId}&pageview_id=${pageviewId}`;

    const adBlockTop = {
      container: 'adsense_top',
      noTitleUnderline: true,
      clicktrackUrl: buildTrackUrl(SectionIds.SECTION_ADSENSE_CLICK_TOP),
      maxTop: this.adBlockSlotsNumber('top', adsCount, isSemUrl),
      ...blockBaseConfig,
      noAdLoadedCallback: () => {
        document.getElementById('adsense_top').style.height = 0;
      },
    };

    const adBlockBottom = {
      container: 'adsense_down',
      noTitleUnderline: true,
      clicktrackUrl: buildTrackUrl(SectionIds.SECTION_ADSENSE_CLICK_BOTTOM),
      number: this.adBlockSlotsNumber('bottom', adsCount),
      ...blockBaseConfig,
      noAdLoadedCallback: () => {
        document.getElementById('adsense_down').style.height = 0;
      },
    };

    /* eslint-disable no-underscore-dangle */
    if (adBlockTop.maxTop === 0 && adBlockBottom.number !== 0) {
      window._googCsa('ads', pageOptions, adBlockBottom);
    } else if (adBlockTop.maxTop >= 1 && adBlockBottom.number >= 1) {
      window._googCsa('ads', pageOptions, adBlockTop, adBlockBottom);
    } else if (adBlockTop.maxTop >= 1) {
      window._googCsa('ads', pageOptions, adBlockTop);
    }
    /* eslint-enable no-underscore-dangle */
  }

  adBlockSlotsNumber(position, adsCount, isSemUrl = false) {
    const { config, shouldShowAFS } = this.props;

    // Avoid show afs when comming from email alerts in Jobs ES, US.
    // @see https://jira.services/browse/PROD-514
    if (!shouldShowAFS) {
      return 0;
    }

    // Set limits base on ads count (https://confluence.services/display/PROD/Google+AdSense)
    if (adsCount <= 3 || isSemUrl) {
      return 0;
    }
    if (adsCount <= 5) {
      return position === 'top' ? 0 : 1;
    }
    if (adsCount <= 10) {
      return position === 'top' ? 1 : 1;
    }

    return get(config, [position]);
  }

  render() {
    const {
      children,
      config,
      dfpCustomTargeting,
      isSemUrl,
      isRetentionUrl,
      isFetching,
      adsCount,
      dfpNetworkId,
      shouldShowDFP,
      personalizedAds,
    } = this.props;

    const countryCode = config.countryCode;
    const verticalName = config.verticalName;
    const adUnit = getAdUnit(countryCode, verticalName, isSemUrl, isRetentionUrl);

    const dfpConfig = this.getDfpProperties(adsCount);

    const isNativeAdsClass = config.paid ? '' : 'isNativeAds';
    const isFetchingClass = isFetching ? 'isFetching' : '';
    const shouldShowTopAds = this.adBlockSlotsNumber('top', adsCount, isSemUrl) > 0;
    const shouldShowBottomAds = this.adBlockSlotsNumber('bottom', adsCount) > 0;
    const isHomesVertical = config.verticalName === 'homes';

    // We add this page ad block specific custom targeting data
    const topCustomTarget = Object.assign({}, dfpCustomTargeting, {
      pos: 'top',
      banner: 'm_serp_top_banner',
    });

    const bellyCustomTarget = Object.assign({}, dfpCustomTargeting, {
      pos: 'belly',
      banner: 'm_serp_belly_banner',
    });

    const bottomCustomTarget = Object.assign({}, dfpCustomTargeting, {
      pos: 'bottom',
      banner: 'm_serp_bottom_banner',
    });

    const dfpConfigs = {
      top: {
        target: topCustomTarget,
        sizes: dfpConfig.top.sizes,
        visible: dfpConfig.top.visible,
      },
      belly: {
        target: bellyCustomTarget,
        sizes: dfpConfig.belly.sizes,
        visible: dfpConfig.belly.visible,
      },
      bottom: {
        target: bottomCustomTarget,
        sizes: dfpConfig.bottom.sizes,
        visible: dfpConfig.bottom.visible,
      },
    };

    const childrenWithDfp = React.Children.map(children, child =>
      React.cloneElement(child, {
        dfpBellyComponentRef: this.serpBellyDfpBanner,
        dfpBellyComponent: dfpConfigs.belly.visible ? (
          <AdSlot
            adUnit={adUnit}
            sizes={dfpConfigs.belly.sizes}
            targetingArguments={dfpConfigs.belly.target}
            onSlotIsViewable={() => {
              this.serpBellyDfpBanner.current.style.padding = '10px 0 6px';
            }}
          />
        ) : (
          {}
        ),
      })
    );

    return (
      <DFPSlotsProvider
        dfpNetworkId={dfpNetworkId}
        autoLoad={shouldShowDFP}
        personalizedAds={personalizedAds}
      >
        <div className={`withAdsense ${isNativeAdsClass}`}>
          {!isFetching && dfpConfigs.top.visible && (
            <div className="dfp" ref={this.serpTopDfpBanner} style={{ padding: '0px' }}>
              <AdSlot
                adUnit={adUnit}
                sizes={dfpConfigs.top.sizes}
                targetingArguments={dfpConfigs.top.target}
                onSlotIsViewable={() => {
                  this.serpTopDfpBanner.current.style.padding = '10px 0 6px';
                }}
              />
            </div>
          )}
          {shouldShowTopAds && (
            <div className={`adsense top ${isFetchingClass}`}>
              <div id="adsense_top" data-test="adsense-top" />
            </div>
          )}
          {childrenWithDfp}
          {shouldShowBottomAds && (
            <div className={`adsense bottom ${isFetchingClass}`}>
              <div id="adsense_down" data-test="adsense-bottom" />
            </div>
          )}
          <div className="dfp" ref={this.serpBottomDfpBanner} style={{ padding: '0px' }}>
            {!isFetching && dfpConfigs.bottom.visible ? (
              <AdSlot
                adUnit={adUnit}
                sizes={dfpConfigs.bottom.sizes}
                targetingArguments={dfpConfigs.bottom.target}
                onSlotIsViewable={() => {
                  this.serpBottomDfpBanner.current.style.padding = '10px 0 15px';
                }}
              />
            ) : (
              <span />
            )}
          </div>
          <style jsx>
            {`
              .withAdsense {
                background: ${colors.grayExtraLight};
                padding: 7px 0 0;
              }

              .adsense {
                background: ${colors.grayExtraLight};
                padding: ${isHomesVertical ? '0 6px 6px' : '0 8px 8px'};
              }

              .adsense div {
                display: block;
                box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
                background: #fff;
                border-radius: ${Theme.global.borderRadius};
                overflow: hidden;
              }

              .adsense.bottom {
                padding-bottom: 10px;
              }

              .adsense.isFetching div {
                opacity: 0.5;
                box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
              }

              .adsense :global(iframe) {
                padding-left: 8px;
                padding-right: 8px;
              }

              .dfp :global(iframe) {
                display: block;
                margin: auto;
              }

              .isNativeAds .adsense :global(iframe) {
                padding: 0;
              }

              .dfp {
                padding: 10px 0 6px;
                text-align: center;
              }
            `}
          </style>
        </div>
      </DFPSlotsProvider>
    );
  }
}

WithThirdPartyAds.defaultProps = {
  adsCount: 0,
};

WithThirdPartyAds.propTypes = {
  children: PropTypes.object.isRequired,
  config: PropTypes.object.isRequired,
  dfpCustomTargeting: PropTypes.object.isRequired,
  isSemUrl: PropTypes.bool.isRequired,
  isRetentionUrl: PropTypes.bool.isRequired,
  isFetching: PropTypes.bool.isRequired,
  shouldShowDFP: PropTypes.bool.isRequired,
  shouldShowAFS: PropTypes.bool.isRequired,
  dfpNetworkId: PropTypes.string.isRequired,
  adsCount: PropTypes.number,
  intl: intlShape.isRequired,
  pageviewId: PropTypes.string.isRequired,
  appEnvironment: PropTypes.string.isRequired,
  personalizedAds: PropTypes.bool.isRequired,
};

export default injectIntl(WithThirdPartyAds);



// WEBPACK FOOTER //
// ./app/Serp/components/WithThirdPartyAds.js